/**
 * LiveCameraOverlay (web) — the snapshot Live Vision experience, ported from the
 * RN MenuScanScreen. Opens the rear camera (getUserMedia), tap anything to
 * identify it (capture frame → /scan with location + place + focus point), shows
 * a "what's what" + result HUD, a corner minimap of interest/intent-driven
 * nearby POIs (tap to expand + guide), compass facing, and pinch/lens zoom.
 *
 * Realtime (Gemini Live) stays in LiveVideoOverlay; this is the tap-to-scan path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Text from '../../common/Text';
import Icon from '../../common/Icon';
import Spinner from '../../common/Spinner';
import SceneInfoCard from './SceneInfoCard';
import MenuRecommendationsCard from './MenuRecommendationsCard';
import NearbyMap, { type NearbyMapMarker } from '../explore/NearbyMap';
import './LiveCameraOverlay.css';
import { useLanguage } from '../../hooks/useLanguage';
import { useLocation } from '../../context/LocationContext';
import {
  scanScene,
  scanMenu,
  getNearbyPois,
  type NearbyPoi,
} from '../../api/menuApi';

interface Props {
  visible: boolean;
  onClose: () => void;
  sessionId: string | null;
  mode?: 'scene' | 'menu';
}

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;
function bearing(a: LatLng, b: LatLng): number {
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}
function distance(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
const fmtDist = (m: number) =>
  m < 1000 ? `${Math.round(m)}m` : `${(m / 1000).toFixed(1)}km`;

interface LatLng {
  lat: number;
  lng: number;
}

export default function LiveCameraOverlay({
  visible,
  onClose,
  sessionId,
  mode = 'scene',
}: Props) {
  const { t } = useLanguage();
  const { location, heading } = useLocation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState<{ x: number; y: number } | null>(null);
  const [response, setResponse] = useState<string | null>(null);
  const [cards, setCards] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  // Hardware zoom range when the track supports it ({min,max,step}); null =
  // fall back to CSS digital zoom (range [1,5]).
  const [zoomCaps, setZoomCaps] = useState<{
    min: number;
    max: number;
    step: number;
  } | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const [devices, setDevices] = useState<string[]>([]);
  const [deviceIdx, setDeviceIdx] = useState(0);
  const [pois, setPois] = useState<NearbyPoi[]>([]);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<NearbyPoi | null>(null);

  const userLatLng = location
    ? { lat: location.latitude, lng: location.longitude }
    : null;

  // ---- Camera lifecycle ------------------------------------------------
  const startCamera = useCallback(async (deviceId?: string) => {
    try {
      streamRef.current?.getTracks().forEach(tk => tk.stop());
      const constraints: MediaStreamConstraints = {
        audio: false,
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: 'environment' } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      // Hardware zoom: real optical/sensor zoom when the track exposes it
      // (Chrome Android, some others). Otherwise we digital-zoom via CSS.
      const track = stream.getVideoTracks()[0] || null;
      videoTrackRef.current = track;
      try {
        const caps: any = track?.getCapabilities?.();
        if (caps && typeof caps.zoom === 'object' && caps.zoom) {
          const min = caps.zoom.min ?? 1;
          const max = caps.zoom.max ?? min;
          const step = caps.zoom.step || 0.1;
          if (max > min) {
            setZoomCaps({ min, max, step });
            setZoom(min);
          } else {
            setZoomCaps(null);
            setZoom(1);
          }
        } else {
          setZoomCaps(null);
          setZoom(1);
        }
      } catch {
        setZoomCaps(null);
        setZoom(1);
      }
      // Enumerate back cameras once we have permission (labels need it).
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all
          .filter(d => d.kind === 'videoinput')
          .filter(d => !/front|face/i.test(d.label))
          .map(d => d.deviceId)
          .filter(Boolean);
        if (cams.length) setDevices(cams);
      } catch {
        /* labels unavailable — single camera */
      }
    } catch {
      /* permission denied / no camera */
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    startCamera();
    return () => {
      streamRef.current?.getTracks().forEach(tk => tk.stop());
      streamRef.current = null;
    };
  }, [visible, startCamera]);

  // ---- Nearby POIs (backend interest/intent-driven) --------------------
  useEffect(() => {
    if (!visible || !sessionId || !userLatLng) return;
    let cancelled = false;
    getNearbyPois(sessionId, userLatLng.lat, userLatLng.lng, mode).then(p => {
      if (!cancelled) setPois(p.slice(0, 8));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    sessionId,
    mode,
    userLatLng ? Math.round(userLatLng.lat * 100) : null,
    userLatLng ? Math.round(userLatLng.lng * 100) : null,
  ]);

  // ---- Capture + scan --------------------------------------------------
  const capture = (): string | null => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    // Hardware zoom already affects the frame; CSS digital zoom doesn't, so
    // crop the center region so the captured image matches what's on screen.
    const cssZoom = zoomCaps ? 1 : zoom;
    const sw = v.videoWidth / cssZoom;
    const sh = v.videoHeight / cssZoom;
    const sx = (v.videoWidth - sw) / 2;
    const sy = (v.videoHeight - sh) / 2;
    const w = Math.min(1280, sw);
    const h = (sh / sw) * w;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.5).split(',')[1] || null;
  };

  const runScan = async (focusPoint?: { x: number; y: number }) => {
    if (busy || !sessionId) return;
    setBusy(true);
    try {
      const base64 = capture();
      if (!base64) throw new Error('capture failed');
      const scan = mode === 'menu' ? scanMenu : scanScene;
      const res = await scan(
        sessionId,
        { data: base64, mime: 'image/jpeg' },
        {
          location: userLatLng
            ? { latitude: userLatLng.lat, longitude: userLatLng.lng }
            : undefined,
          focusPoint,
        },
      );
      const found = (res.content || []).filter(
        (c: any) =>
          c.type === 'menu_recommendations' || c.type === 'scene_info',
      );
      setResponse(res.response || null);
      setCards(found);
      setExpanded(false);
    } catch {
      /* surfaced via empty result */
    } finally {
      setBusy(false);
      setFocus(null);
    }
  };

  const onTapPreview = (e: React.MouseEvent) => {
    if (busy || mapExpanded || expanded) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    let fp: { x: number; y: number } | undefined;
    if (rect && rect.width && rect.height) {
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setFocus({ x: px, y: py });
      fp = {
        x: Math.min(1, Math.max(0, px / rect.width)),
        y: Math.min(1, Math.max(0, py / rect.height)),
      };
    }
    runScan(fp);
  };

  // ---- Zoom (hardware where supported, else CSS) + lens cycle ----------
  // Apply a target zoom: real sensor zoom via applyConstraints when the track
  // supports it, otherwise CSS digital scale (clamped 1..5).
  const applyZoom = useCallback(
    (next: number) => {
      const caps = zoomCaps;
      if (caps) {
        const v = Math.min(caps.max, Math.max(caps.min, next));
        setZoom(v);
        const track = videoTrackRef.current;
        if (track) {
          track
            .applyConstraints({ advanced: [{ zoom: v } as any] })
            .catch(() => {});
        }
      } else {
        setZoom(Math.min(5, Math.max(1, next)));
      }
    },
    [zoomCaps],
  );

  const pinchRef = useRef<number | null>(null);
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const d = Math.hypot(dx, dy);
    if (pinchRef.current != null) {
      applyZoom(zoom * (d / pinchRef.current));
    }
    pinchRef.current = d;
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
  };
  const onWheel = (e: React.WheelEvent) => {
    const span = zoomCaps ? (zoomCaps.max - zoomCaps.min) / 200 : 0.002;
    applyZoom(zoom - e.deltaY * span);
  };
  const cycleLens = () => {
    if (devices.length < 2) return;
    const next = (deviceIdx + 1) % devices.length;
    setDeviceIdx(next);
    setZoom(1);
    startCamera(devices[next]);
  };

  // ---- Derived: subject, guidance -------------------------------------
  const subjectTitle = useMemo(() => {
    const s = cards.find((c: any) => c.type === 'scene_info') as any;
    return s?.title || s?.data?.title || null;
  }, [cards]);

  const guide = useMemo(() => {
    if (!selectedTarget || !userLatLng) return null;
    const to = { lat: selectedTarget.lat, lng: selectedTarget.lng };
    const brg = bearing(userLatLng, to);
    const dist = distance(userLatLng, to);
    let rel: number | null = null;
    if (heading != null) {
      const r = (brg - heading + 360) % 360;
      rel = r > 180 ? r - 360 : r;
    }
    return { title: selectedTarget.name, brg, dist, rel };
  }, [selectedTarget, userLatLng, heading]);

  const markers: NearbyMapMarker[] = useMemo(() => {
    const out: NearbyMapMarker[] = [];
    if (userLatLng)
      out.push({
        id: 'me',
        lat: userLatLng.lat,
        lng: userLatLng.lng,
        color: '#aa12ff',
      });
    pois.forEach((p, i) =>
      out.push({
        id: `poi-${i}`,
        lat: p.lat,
        lng: p.lng,
        color: selectedTarget?.name === p.name ? '#ff3b5c' : '#3b82f6',
        onClick: () => {
          setSelectedTarget(p);
          setMapExpanded(false);
        },
      }),
    );
    return out;
  }, [pois, userLatLng, selectedTarget]);

  const sortedPois = useMemo(() => {
    if (!userLatLng) return pois.map(p => ({ p, d: null as number | null }));
    return pois
      .map(p => ({ p, d: distance(userLatLng, { lat: p.lat, lng: p.lng }) }))
      .sort((a, b) => (a.d ?? 0) - (b.d ?? 0));
  }, [pois, userLatLng]);

  if (!visible) return null;

  const hasResult = cards.length > 0 || !!response;
  const facing =
    heading != null ? CARDINALS[Math.round(heading / 45) % 8] : null;

  return (
    <div style={ROOT} role="dialog" aria-modal="true">
      <div
        ref={wrapRef}
        style={CAMERA_WRAP}
        onClick={onTapPreview}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            ...VIDEO,
            transform: zoomCaps ? 'none' : `scale(${zoom})`,
          }}
        />

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          style={CLOSE_BTN}
          aria-label={t('CLOSE', 'Close')}
        >
          <Icon name="close" size={24} color="#fff" />
        </button>

        {/* Compass */}
        {facing && (
          <div style={COMPASS}>
            <Icon name="compass-outline" size={13} color="#fff" />
            <Text
              variant="body"
              style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}
            >
              {t(`COMPASS_${facing}`, facing)}
            </Text>
          </div>
        )}

        {/* Zoom / lens */}
        <div style={LENS_CONTROLS} onClick={e => e.stopPropagation()}>
          {(() => {
            // Display magnification relative to the lens base (hardware zoom is
            // reported in arbitrary units starting at its min).
            const disp = zoomCaps ? zoom / zoomCaps.min : zoom;
            return Math.abs(disp - 1) > 0.05 ? (
              <div style={ZOOM_CHIP}>
                <Text
                  variant="body"
                  style={{ color: '#fff', fontWeight: 600, fontSize: 12 }}
                >
                  {disp.toFixed(1)}×
                </Text>
              </div>
            ) : null;
          })()}
          {devices.length > 1 && (
            <button
              type="button"
              style={LENS_BTN}
              onClick={cycleLens}
              aria-label={t('SWAP_LENS', 'Swap camera')}
            >
              <Icon name="camera-reverse-outline" size={18} color="#fff" />
            </button>
          )}
        </div>

        {/* Corner minimap */}
        {userLatLng && (
          <div
            style={MINIMAP}
            onClick={e => {
              e.stopPropagation();
              setMapExpanded(true);
            }}
          >
            <NearbyMap
              center={userLatLng}
              zoom={15}
              markers={markers}
              className="wg-live-minimap"
            />
            <div style={EXPAND_HINT}>
              <Icon name="expand-outline" size={12} color="#fff" />
            </div>
          </div>
        )}

        {/* What's what */}
        {!!subjectTitle && (
          <div style={SUBJECT_CHIP}>
            <Icon name="sparkles-outline" size={14} color="#aa12ff" />
            <Text
              variant="body"
              style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}
            >
              {subjectTitle}
            </Text>
          </div>
        )}

        {/* Guidance chip + AR reticle */}
        {guide && !expanded && (
          <div style={GUIDE_CHIP} onClick={e => e.stopPropagation()}>
            <span
              style={{
                display: 'inline-block',
                transform: `rotate(${guide.rel ?? guide.brg}deg)`,
              }}
            >
              <Icon name="arrow-up" size={18} color="#fff" />
            </span>
            <Text variant="body" style={{ color: '#fff', fontSize: 13 }}>
              {guide.title} ·{' '}
              {guide.rel != null
                ? Math.abs(guide.rel) <= 30
                  ? t('GUIDE_AHEAD', 'Ahead')
                  : Math.abs(guide.rel) >= 150
                    ? t('GUIDE_BEHIND', 'Behind you')
                    : guide.rel < 0
                      ? t('GUIDE_TURN_LEFT', 'On your left')
                      : t('GUIDE_TURN_RIGHT', 'On your right')
                : ''}{' '}
              · {fmtDist(guide.dist)}
            </Text>
            <button
              type="button"
              style={GUIDE_CLOSE}
              onClick={() => setSelectedTarget(null)}
              aria-label={t('GUIDE_STOP', 'Stop guiding')}
            >
              <Icon name="close" size={16} color="#fff" />
            </button>
          </div>
        )}
        {guide &&
          !expanded &&
          guide.rel != null &&
          Math.abs(guide.rel) <= 30 && (
            <div
              style={{
                ...CAM_TARGET,
                left: `calc(50% + ${(guide.rel / 30) * 42}% )`,
              }}
            >
              <Icon name="locate-outline" size={40} color="#aa12ff" />
              <Text
                variant="body"
                style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}
              >
                {fmtDist(guide.dist)}
              </Text>
            </div>
          )}

        {/* Focus ring + spinner */}
        {!!focus && (
          <div style={{ ...FOCUS_RING, left: focus.x - 30, top: focus.y - 30 }}>
            {busy && <Spinner size="small" color="#fff" />}
          </div>
        )}
        {busy && !focus && (
          <div style={PROCESSING}>
            <Spinner size="large" color="#fff" />
          </div>
        )}

        {/* Hint */}
        {!busy && !expanded && !mapExpanded && (
          <div style={HINT}>
            <Text variant="body" style={{ color: '#fff' }}>
              {mode === 'menu'
                ? t('MENU_TAP_HINT', 'Tap a dish — or the whole menu')
                : t('SCAN_TAP_ANYTHING', 'Tap anything to identify it')}
            </Text>
          </div>
        )}

        {/* Brief result bar */}
        {hasResult && !expanded && (
          <div style={BRIEF_BAR} onClick={e => e.stopPropagation()}>
            <Text variant="body" style={{ color: '#fff', flex: 1 }}>
              {response || t('SCAN_RESULT', 'What you found')}
            </Text>
            {cards.length > 0 && (
              <button
                type="button"
                style={ICON_BTN}
                onClick={() => setExpanded(true)}
                aria-label={t('EXPAND', 'Expand')}
              >
                <Icon name="chevron-expand-outline" size={20} color="#fff" />
              </button>
            )}
            <button
              type="button"
              style={ICON_BTN}
              onClick={() => {
                setCards([]);
                setResponse(null);
              }}
              aria-label={t('CLOSE', 'Close')}
            >
              <Icon name="close" size={20} color="#fff" />
            </button>
          </div>
        )}

        {/* Expanded result card */}
        {hasResult && expanded && (
          <div style={EXPANDED_PANEL} onClick={e => e.stopPropagation()}>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <button
                type="button"
                style={ICON_BTN}
                onClick={() => setExpanded(false)}
                aria-label={t('COLLAPSE', 'Collapse')}
              >
                <Icon name="chevron-collapse-outline" size={22} color="#fff" />
              </button>
            </div>
            <div style={EXPANDED_SCROLL}>
              {cards.map((c, i) =>
                c.type === 'menu_recommendations' ? (
                  <MenuRecommendationsCard key={i} item={c} />
                ) : (
                  <SceneInfoCard key={i} item={c} />
                ),
              )}
            </div>
          </div>
        )}

        {/* Expanded map + POI list */}
        {mapExpanded && (
          <div style={MAP_EXPANDED} onClick={e => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 0',
              }}
            >
              <Text variant="h3" style={{ color: '#fff' }}>
                {t('NEARBY', 'Nearby')}
              </Text>
              <button
                type="button"
                style={ICON_BTN}
                onClick={() => setMapExpanded(false)}
                aria-label={t('CLOSE', 'Close')}
              >
                <Icon name="close" size={24} color="#fff" />
              </button>
            </div>
            <div
              style={{
                height: '38%',
                borderRadius: 14,
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              <NearbyMap
                center={userLatLng}
                zoom={14}
                markers={markers}
                className="wg-live-bigmap"
              />
            </div>
            <div style={POI_LIST}>
              {sortedPois.length === 0 ? (
                <Text
                  variant="body"
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                    textAlign: 'center',
                  }}
                >
                  {t('NO_NEARBY', 'No nearby places yet.')}
                </Text>
              ) : (
                sortedPois.map(({ p, d }, i) => (
                  <button
                    key={i}
                    type="button"
                    style={{
                      ...POI_ROW,
                      ...(selectedTarget?.name === p.name
                        ? POI_ROW_ACTIVE
                        : {}),
                    }}
                    onClick={() => {
                      setSelectedTarget(p);
                      setMapExpanded(false);
                    }}
                  >
                    <Icon
                      name={
                        p.category && /restaurant|cafe|food/i.test(p.category)
                          ? 'restaurant-outline'
                          : 'location-outline'
                      }
                      size={18}
                      color="#aa12ff"
                    />
                    <Text variant="body" style={{ color: '#fff', flex: 1 }}>
                      {p.name}
                    </Text>
                    {d != null && (
                      <Text
                        variant="body"
                        style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}
                      >
                        {fmtDist(d)}
                      </Text>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- styles ------------------------------------------------------------
const ROOT: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 4000,
  background: '#000',
};
const CAMERA_WRAP: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  cursor: 'pointer',
};
const VIDEO: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transformOrigin: 'center',
};
const CLOSE_BTN: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 40,
  height: 40,
  borderRadius: 20,
  border: 'none',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 5,
};
const COMPASS: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  left: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  background: 'rgba(0,0,0,0.55)',
  borderRadius: 8,
  padding: '3px 7px',
};
const LENS_CONTROLS: React.CSSProperties = {
  position: 'absolute',
  left: 16,
  bottom: 90,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-start',
};
const ZOOM_CHIP: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  borderRadius: 12,
  padding: '4px 8px',
};
const LENS_BTN: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 22,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
const MINIMAP: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 64,
  width: 120,
  height: 120,
  borderRadius: 14,
  overflow: 'hidden',
  border: '1px solid rgba(255,255,255,0.25)',
  opacity: 0.92,
};
const EXPAND_HINT: React.CSSProperties = {
  position: 'absolute',
  bottom: 4,
  right: 4,
  background: 'rgba(0,0,0,0.55)',
  borderRadius: 8,
  padding: 3,
  pointerEvents: 'none',
};
const SUBJECT_CHIP: React.CSSProperties = {
  position: 'absolute',
  top: 58,
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: '78%',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(10,12,16,0.7)',
  borderRadius: 14,
  padding: '6px 12px',
  border: '1px solid rgba(170,18,255,0.5)',
};
const GUIDE_CHIP: React.CSSProperties = {
  position: 'absolute',
  top: 100,
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: '84%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(10,12,16,0.72)',
  borderRadius: 16,
  padding: '7px 14px',
  border: '1px solid rgba(170,18,255,0.5)',
};
const GUIDE_CLOSE: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};
const CAM_TARGET: React.CSSProperties = {
  position: 'absolute',
  top: '44%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  pointerEvents: 'none',
};
const FOCUS_RING: React.CSSProperties = {
  position: 'absolute',
  width: 60,
  height: 60,
  borderRadius: 30,
  border: '2px solid #aa12ff',
  background: 'rgba(170,18,255,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
};
const PROCESSING: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.35)',
  pointerEvents: 'none',
};
const HINT: React.CSSProperties = {
  position: 'absolute',
  bottom: 28,
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(0,0,0,0.5)',
  borderRadius: 14,
  padding: '8px 14px',
  pointerEvents: 'none',
};
const BRIEF_BAR: React.CSSProperties = {
  position: 'absolute',
  bottom: 18,
  left: 12,
  right: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(0,0,0,0.62)',
  borderRadius: 16,
  padding: 12,
  border: '1px solid rgba(255,255,255,0.12)',
};
const ICON_BTN: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 18,
  border: 'none',
  background: 'rgba(255,255,255,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
const EXPANDED_PANEL: React.CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '60%',
  background: 'rgba(10,12,16,0.96)',
  borderTopLeftRadius: 18,
  borderTopRightRadius: 18,
  padding: 12,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};
const EXPANDED_SCROLL: React.CSSProperties = {
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingBottom: 8,
};
const MAP_EXPANDED: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(10,12,16,0.97)',
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
};
const POI_LIST: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const POI_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 12,
  border: 'none',
  background: 'rgba(255,255,255,0.06)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};
const POI_ROW_ACTIVE: React.CSSProperties = {
  background: 'rgba(170,18,255,0.18)',
  border: '1px solid rgba(170,18,255,0.5)',
};
